import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { QrCode, Share2 } from 'lucide-react';

interface MenuQrCodeProps {
  trigger?: React.ReactNode;
}

export function MenuQrCode({ trigger }: MenuQrCodeProps) {
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const menuUrl = `${window.location.origin}/restaurant-menu`;

  useEffect(() => {
    QRCode.toDataURL(menuUrl, {
      width: 400,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then(url => setQrDataUrl(url))
      .catch(err => console.error('QR Generation Error:', err));
  }, [menuUrl]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Ikoro House - Menu QR</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap');
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body {
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                font-family: 'Outfit', sans-serif;
                background: white;
                padding: 40px;
              }
              .card {
                border: 3px solid #000;
                border-radius: 24px;
                padding: 48px 40px;
                max-width: 420px;
                width: 100%;
                text-align: center;
              }
              .logo-row {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                margin-bottom: 6px;
              }
              .logo-row img { width: 36px; height: 36px; }
              .brand-name {
                font-size: 22px;
                font-weight: 900;
                letter-spacing: -1px;
                text-transform: uppercase;
                font-style: italic;
              }
              .divider { width: 60px; height: 3px; background: black; margin: 14px auto 28px; }
              .qr-wrap {
                display: inline-block;
                border: 2px solid #f0f0f0;
                border-radius: 16px;
                padding: 12px;
                margin-bottom: 28px;
              }
              .qr-wrap img { width: 260px; height: 260px; display: block; }
              .cta { font-size: 12px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #555; margin-bottom: 8px; }
              .sub { font-size: 10px; color: #aaa; font-weight: 500; }
            </style>
          </head>
          <body>
            <div class="card">
              <div class="logo-row">
                <img src="${window.location.origin}/brand-b.png" alt="Ikoro House" onerror="this.style.display='none'" />
                <span class="brand-name">Ikoro House</span>
              </div>
              <div class="divider"></div>
              <div class="qr-wrap">
                <img src="${qrDataUrl}" alt="Restaurant Menu QR" />
              </div>
              <p class="cta">Scan to View Our Menu</p>
              <p class="sub">Kitchen &amp; Bar &nbsp;&middot;&nbsp; Kigali, Rwanda</p>
            </div>
            <script>window.onload = () => { window.print(); };</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        {trigger || (
          <Button variant="outline" size="icon">
            <QrCode className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm bg-white text-black border border-gray-100 shadow-2xl rounded-2xl overflow-hidden p-0">
        {/* Branded Header */}
        <DialogHeader className="bg-black p-6 text-white">
          <DialogTitle className="text-center flex flex-col items-center gap-2">
            <div className="flex items-center gap-3">
              <img
                src="/brand-b.png"
                alt="Ikoro House"
                className="w-8 h-8 invert"
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
              <span className="text-xl font-black tracking-tighter uppercase italic">Ikoro House</span>
            </div>
            <span className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase">
              Kitchen &amp; Bar · Menu QR
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center p-8 space-y-6">
          {/* QR Code */}
          <div className="bg-white p-3 rounded-2xl border-2 border-gray-50 shadow-xl overflow-hidden">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="Menu QR Code" className="w-[190px] h-[190px]" />
            ) : (
              <div className="w-[190px] h-[190px] flex items-center justify-center bg-gray-50">
                <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          <p className="text-[11px] font-black tracking-widest uppercase text-gray-400 text-center">
            Scan to view our menu
          </p>

          {/* Buttons */}
          <div className="w-full">
            <Button
              onClick={handlePrint}
              className="w-full h-12 bg-black hover:bg-gray-900 text-white font-bold rounded-xl shadow-lg shadow-black/10 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <img
                src="/brand-b.png"
                alt=""
                className="w-4 h-4 invert"
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
              Print QR
            </Button>
          </div>

          <p className="text-[11px] text-gray-400 text-center leading-relaxed font-medium">
            Display this QR code so guests can access<br /> the digital menu instantly.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
